import { UserProfile, Material } from '../types';
import { COST_VARIABLES_MATRIX, NICHES as MATRIX_NICHES } from '../constants/costMatrix';

export interface PriceHealthData {
  level: 1 | 2 | 3;
  percentage: number;
  label: string;
  description: string;
}

/**
 * Calcula a "Saúde do Preço" baseada no preenchimento da Matriz de Custos
 * e na existência de materiais no Centro de Custos.
 */
export const calculatePriceHealth = (user: UserProfile, materials: Material[]): PriceHealthData => {
  if (!user.niche) {
    return {
      level: 1,
      percentage: 0,
      label: "Profissão não definida",
      description: "Selecione sua profissão para calcular o preço certo."
    };
  }

  // 1. Localiza a configuração da matriz para o nicho atual
  const nicheObj = MATRIX_NICHES.find(n => n.name === user.niche);
  if (!nicheObj) {
    return {
      level: 1,
      percentage: 0,
      label: "Profissão não encontrada",
      description: "Verifique a configuração da sua área de atuação."
    };
  }

  const nicheVars = COST_VARIABLES_MATRIX.filter(v => v.nicheId === nicheObj.id);
  if (nicheVars.length === 0) {
    return {
      level: 1,
      percentage: 30, // Base se não houver matriz definida para o nicho
      label: "Quase lá",
      description: "Sua área de trabalho está sendo configurada. Continue orçando!"
    };
  }

  // 2. Cálculo Moderno de Saúde do Preço R-MAX (Zero Friction)
  let score = 0;

  // Critério A: Base Financeira Sólida (30%)
  const isFinancialComplete = (user.taxRegime && user.niche && user.profession);
  if (isFinancialComplete) score += 30;

  // Critério B: Diversidade do Inventário (40%)
  // Verifica se o usuário tem tanto itens fixos quanto variáveis
  const hasFixed = materials.some(m => m.category === 'fixo');
  const hasVariable = materials.some(m => m.category === 'variável');
  const hasDepreciation = materials.some(m => m.depreciationMonths && m.depreciationMonths > 1);

  if (hasVariable) score += 20; // Tem insumos
  if (hasFixed) score += 10;    // Tem custos fixos
  if (hasDepreciation) score += 10; // Está usando o cálculo de depreciação

  // Critério C: Poder de Venda / Histórico (30%)
  // Como não temos acesso a recipes/services aqui, usamos o materials e a existência de campos de user como proxy
  // mas principalmente se o usuário já começou a povoar o sistema.
  if (materials.length > 5) score += 15;
  if (materials.length > 10) score += 15;

  let percentage = score;

  // 4. Determina Nível e Copy Dinâmico
  let level: 1 | 2 | 3 = 1;
  let label = "";
  let description = "";

  if (percentage >= 100) {
    level = 3;
    label = "Preço Seguro!";
    description = "Lucro Real Garantido nas suas vendas.";
  } else if (percentage >= 50) {
    level = 2;
    label = "Quase lá!";
    description = "Sua base financeira está configurada.";
  } else {
    level = 1;
    label = "Preço Perigoso!";
    description = "Faltam custos fixos no seu cálculo.";
  }

  return { level, percentage: Math.min(100, percentage), label, description };
};
